import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchPloById, savePloDraft } from '../../../services/PloThreeService';
import { useQueryClient } from '@tanstack/react-query';
import { DatalistInput } from '../../cases/components/dataListInput';
import { useSettings } from '../../../context/SettingContext';

// Table Row Interface
interface PLORowState {
  id: number;
  caseYear: string;
  caseNumber: string;
  caseType: string;
  offenseCategory: string;
  lawSection: string;
  adviceRequestedDate: string;
  turnedDownDate: string;
  adviceGivenDate: string;
  prosecutedDate: string;
  courtCaseNumber: string;
  plaintiffName: string;
  defendantName: string;
  defendantStatus: string;
  remandStatus: string;
  trialPeriod: string;
  residentArea: string;
}

interface PLOForm3State {
  officeId: string;
  officeName: string;
  rows: PLORowState[];
}

export default function PLOForm3() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>(); // Reads :id from route e.g. /plo-form-three/edit/:id

  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const [formData, setFormData] = useState<PLOForm3State>({
    officeId: '',
    officeName: '',
    rows: [
      {
        id: 1,
        caseYear: '',
        caseNumber: '',
        caseType: '',
        offenseCategory: '',
        lawSection: '',
        adviceRequestedDate: '',
        turnedDownDate: '',
        adviceGivenDate: '',
        prosecutedDate: '',
        courtCaseNumber: '',
        plaintiffName: '',
        defendantName: '',
        defendantStatus: '',
        remandStatus: '',
        trialPeriod: '',
        residentArea: ''
      }
    ]
  });

  // Fetch record when editing existing ID
  useEffect(() => {
  if (!id) return;

  const loadSingleRecord = async () => {
    try {
      setIsLoadingRecord(true);
      const res = await fetchPloById(id);
      const data = res?.data || res;

      if (data) {
        const formDetails = data.ploFormThree || data;

        // 1. Get officeId (reads data.officeId directly from flattened object)
        const currentOfficeId = data.officeId || data.ploFormThree?.officeId || data.originOfficeId || '';

        // 2. Fixed log: log the extracted currentOfficeId directly
        console.log("this is full data:", data);
        console.log("this is office id:", currentOfficeId); // Logs: "cmso7yfpk000owoc3v33olrvp"

        // 3. Find matching office name in dropdown options
        const matchedOffice = settings?.offices?.find(
          (o) => o.id === currentOfficeId
        );

        setFormData({
          officeId: currentOfficeId,
          officeName: matchedOffice ? matchedOffice.name : '',
          rows: [
            {
              id: 1,
              caseYear: formDetails.caseYear || '',
              caseNumber: formDetails.caseNumber || '',
              caseType: formDetails.caseType || '',
              offenseCategory: formDetails.offenseCategory || '',
              lawSection: formDetails.lawSection || '',
              adviceRequestedDate: formDetails.adviceRequestedDate || '',
              turnedDownDate: formDetails.turnedDownDate || '',
              adviceGivenDate: formDetails.adviceGivenDate || '',
              prosecutedDate: formDetails.prosecutedDate || '',
              courtCaseNumber: formDetails.courtCaseNumber || '',
              plaintiffName: formDetails.plaintiffName || '',
              defendantName: formDetails.defendantName || '',
              defendantStatus: formDetails.defendantStatus || '',
              remandStatus: formDetails.remandStatus || '',
              trialPeriod: formDetails.trialPeriod || '',
              residentArea: formDetails.residentArea || ''
            }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to load record for editing:', error);
      alert('အချက်အလက်များ ဆွဲယူ၍ မရရှိပါ။');
    } finally {
      setIsLoadingRecord(false);
    }
  };

  loadSingleRecord();
}, [id, settings?.offices]); // Include settings so officeName updates when settings load

  const handleOfficeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const selectedName = e.target.value;

    // Find matching office object from settings
    const matchedOffice = settings?.offices.find(
      (o) => o.name.trim().toLowerCase() === selectedName.trim().toLowerCase()
    );

    setFormData((prev) => ({
      ...prev,
      officeName: selectedName,
      officeId: matchedOffice ? matchedOffice.id : '', // Attach ID if matched, else reset
    }));
  };

  const handleRowChange = (rowId: number, field: keyof PLORowState, value: string) => {
    const updatedRows = formData.rows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setFormData({ ...formData, rows: updatedRows });
  };

  const handleAddRow = () => {
    const newId = formData.rows.length > 0 ? Math.max(...formData.rows.map(r => r.id)) + 1 : 1;
    const newRow: PLORowState = {
      id: newId,
      caseYear: '',
      caseNumber: '',
      caseType: '',
      offenseCategory: '',
      lawSection: '',
      adviceRequestedDate: '',
      turnedDownDate: '',
      adviceGivenDate: '',
      prosecutedDate: '',
      courtCaseNumber: '',
      plaintiffName: '',
      defendantName: '',
      defendantStatus: '',
      remandStatus: '',
      trialPeriod: '',
      residentArea: ''
    };
    setFormData({ ...formData, rows: [...formData.rows, newRow] });
  };

  const handleRemoveRow = (rowId: number) => {
    if (formData.rows.length === 1) {
      alert("အနည်းဆုံး အချက်အလက် Row တစ်ခု ရှိနေရပါမည်။");
      return;
    }
    setFormData({
      ...formData,
      rows: formData.rows.filter(row => row.id !== rowId)
    });
  };

  // Handle Save / Update Form
  const handleSaveForm = async () => {
    try {
      setIsSaving(true);

    const payload = formData.rows.map((row) => ({
      ...row,
      officeId: formData.officeId || undefined, // Sends officeId (or undefined/null if empty)
      id: id || undefined,
      status: 'DRAFT',
    }));

      // Pass `id` if editing existing record, or `null` if creating new
      await savePloDraft(id || null, payload);
      await queryClient.invalidateQueries({
        queryKey: ['ploFormThree'],
      });
      alert(id ? 'အချက်အလက်များ ပြင်ဆင်သိမ်းဆည်းပြီးပါပြီ။' : 'အချက်အလက်များကို အောင်မြင်စွာ သိမ်းဆည်းပြီးပါပြီ။');
      navigate(-1); // Go back to table view after saving
    } catch (error: any) {
      console.error('Failed to save PLO Form 3:', error);
      alert(error?.message || 'ဒေတာများ သိမ်းဆည်းရာတွင် အမှားအယွင်းရှိနေပါသည်။');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingRecord) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center font-sans text-sm text-slate-600">
        အချက်အလက်များ ပြန်လည်ဆွဲယူနေပါသည်...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 flex flex-col items-center justify-start font-serif text-black">
      
      {/* Top Controller */}
      <div className="w-full max-w-[1400px] mb-4 flex justify-between items-center font-sans">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-2 rounded shadow-sm transition"
          >
            ← နောက်သို့ (Back)
          </button>
          <h2 className="text-lg font-bold text-slate-800">
            {id ? 'ရှေ့ပြေးဥပဒေရုံးအမှုမှတ်တမ်း ပြင်ဆင်ရန် (Edit PLO Form 3)' : 'ရှေ့ပြေးဥပဒေရုံးအမှုမှတ်တမ်း (PLO Form 3)'}
          </h2>
        </div>

        <button
          type="button"
          onClick={() => alert("ပရင့်ထုတ်ရန် အစမ်းကြည့်ရှုခြင်း...")}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded shadow transition"
        >
          📄 Print Preview
        </button>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-[1400px] bg-white shadow-xl border border-gray-300 p-8 space-y-6 overflow-x-auto">
        
        {/* Header Section */}
        <div className="flex justify-between items-start text-xs font-bold">
          <div>
            <div className="flex items-center gap-2">
              <span>ရှေ့ပြေး ဥပဒေရုံးအမည် -</span>
              <DatalistInput
                name="office"
                value={formData.officeName}
                onChange={handleOfficeChange}
                placeholder=""
                options={OFFICE_OPTIONS}
              />
            </div>
          </div>
          <div className="text-center space-y-1">
            {/* <div className="border border-black px-3 py-1 text-[11px]">PTI ပုံစံ</div> */}
            <div className="text-[11px] pt-1">ရှေ့ပြေး ဥပဒေရုံးများအတွက် အမှုမှတ်တမ်း</div>
          </div>
        </div>

        {/* Table Section */}
        <div className="w-full overflow-x-auto border border-black">
          <table className="w-full text-left border-collapse text-[11px] min-w-[1350px]">
            <thead>
              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold text-xs">
                <th rowSpan={2} className="border-r border-black p-1 w-8">စဉ်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-100">ဥပဒေရုံးအမှုအမှတ်</th>
                <th colSpan={8} className="border-r border-black p-1 bg-slate-200">အမှုအချက်အလက်</th>
                <th colSpan={6} className="border-r border-black p-1 bg-slate-100">အမှုသည် အချက်အလက်</th>
                <th rowSpan={3} className="p-1 w-12 font-sans text-gray-500">Action</th>
              </tr>

              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold">
                <th className="border-r border-black p-1 w-14">အမှုခုနှစ်</th>
                <th className="border-r border-black p-1 w-16">အမှုနံပါတ်</th>
                <th className="border-r border-black p-1 w-20">အမှုအမျိုးအစား<br/><span className="text-[9px] font-normal">၁။ရာကြီး၊ ၂။ရာထွေ</span></th>
                <th className="border-r border-black p-1 w-24">အမှုအမျိုးအမည်<br/><span className="text-[9px] font-normal">၁။လူသတ်မှု၊ ၂။မုဒိမ်းမှု၊ ၃။သက်ငယ်မုဒိမ်းမှု ၄။ခိုးမှု ၅။လိမ်လည်မှု ၆။လူကုန်ကူးမှု ၇။အခြား</span></th>
                <th className="border-r border-black p-1 w-14">ဥပဒေ/ပုဒ်မ</th>
                <th className="border-r border-black p-1 w-20">အကြံပေးတောင်းခံသည့်ရက်စွဲ</th>
                <th className="border-r border-black p-1 w-16">Turned down ချသည့်နေ့စွဲ</th>
                <th className="border-r border-black p-1 w-18">အကြံပေးချသည့်နေ့စွဲ</th>
                <th className="border-r border-black p-1 w-20">တရားစွဲ တင်သည့်နေ့စွဲ</th>
                <th className="border-r border-black p-1 w-20">တရားရုံးအမှုအမှတ်</th>
                <th className="border-r border-black p-1 w-20">တရားလိုအမည်</th>
                <th className="border-r border-black p-1 w-20">တရားခံအမည်</th>
                <th className="border-r border-black p-1 w-16">တရားခံ ဖမ်းဆီးသည့်နေ့</th>
                <th className="border-r border-black p-1 w-20">၁ ရမန် ၂ အာမခံ</th>
                <th className="border-r border-black p-1 w-16">ရမန် ကာလ</th>
                <th className="border-r border-black p-1 w-16">၁နစ်နာသူ ၂သက်သေ</th>
              </tr>
            </thead>
            <tbody>
              {formData.rows.map((row, index) => (
                <tr key={row.id} className="border-b border-gray-400 hover:bg-slate-50 text-center">
                  <td className="border-r border-black p-1 font-bold">{index + 1}</td>
                  
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.caseYear} onChange={(e) => handleRowChange(row.id, 'caseYear', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.caseNumber} onChange={(e) => handleRowChange(row.id, 'caseNumber', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.caseType} onChange={(e) => handleRowChange(row.id, 'caseType', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.offenseCategory} onChange={(e) => handleRowChange(row.id, 'offenseCategory', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.lawSection} onChange={(e) => handleRowChange(row.id, 'lawSection', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.adviceRequestedDate} onChange={(e) => handleRowChange(row.id, 'adviceRequestedDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.turnedDownDate} onChange={(e) => handleRowChange(row.id, 'turnedDownDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.adviceGivenDate} onChange={(e) => handleRowChange(row.id, 'adviceGivenDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.prosecutedDate} onChange={(e) => handleRowChange(row.id, 'prosecutedDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.courtCaseNumber} onChange={(e) => handleRowChange(row.id, 'courtCaseNumber', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.plaintiffName} onChange={(e) => handleRowChange(row.id, 'plaintiffName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.defendantName} onChange={(e) => handleRowChange(row.id, 'defendantName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.defendantStatus} onChange={(e) => handleRowChange(row.id, 'defendantStatus', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.remandStatus} onChange={(e) => handleRowChange(row.id, 'remandStatus', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.trialPeriod} onChange={(e) => handleRowChange(row.id, 'trialPeriod', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="border-r border-black p-1">
                    <input type="text" value={row.residentArea} onChange={(e) => handleRowChange(row.id, 'residentArea', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  <td className="p-1 font-sans">
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(row.id)}
                      className="text-red-600 hover:text-red-800 font-medium text-xs focus:outline-none"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Row Button */}
        {!id && (
        <div className="flex justify-start font-sans">
          <button
            type="button"
            onClick={handleAddRow}
            className="bg-slate-800 hover:bg-slate-950 text-white text-xs font-medium px-4 py-2 rounded shadow transition flex items-center gap-1"
          >
            ➕ အတန်းအသစ်ထည့်ရန် (Add New Row)
          </button>
        </div>
      )}

        <div className="text-left text-[11px] font-bold text-gray-600 pt-4">
          * PTI အမှုမှတ်တမ်း
        </div>
      </div>

      {/* Save Button */}
      <div className="w-full max-w-[1400px] mt-4 flex justify-end font-sans">
        <button
          type="button"
          onClick={handleSaveForm}
          disabled={isSaving}
          className={`bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-6 py-2.5 rounded shadow-md transition flex items-center gap-2 ${
            isSaving ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSaving ? 'သိမ်းဆည်းနေပါသည်...' : id ? '💾 ပြင်ဆင်ချက်များ သိမ်းဆည်းမည်' : '💾 ဒေတာများအားလုံးသိမ်းဆည်းမည်'}
        </button>
      </div>

    </div>
  );
}